import { BrowserProvider, Contract, Wallet } from "ethers";
import { WORK_ESCROW_ABI } from "./escrow-contract.js";
import {
  createSessionGrant,
  sessionGrantTypedData
} from "./session-key.js";

const EIP712_DOMAIN_FIELDS = Object.freeze([
  Object.freeze({ name: "name", type: "string" }),
  Object.freeze({ name: "version", type: "string" }),
  Object.freeze({ name: "chainId", type: "uint256" }),
  Object.freeze({ name: "verifyingContract", type: "address" })
]);

export async function authorizeWorkerSession(networkConfig, eip1193Provider, worker) {
  if (!networkConfig?.settlement?.sessionRelayEnabled) throw new Error("Unattended session relay is not configured.");
  if (!eip1193Provider?.request) throw new Error("A connected wallet is required to authorize a worker shift.");
  const contractAddress = networkConfig.settlement.contractAddress;
  const provider = new BrowserProvider(eip1193Provider);
  const contract = new Contract(contractAddress, WORK_ESCROW_ABI, provider);
  const epoch = await contract.sessionEpoch(worker);
  const signer = Wallet.createRandom();
  const grant = createSessionGrant({ worker, sessionKey: signer.address, epoch });
  const typedData = sessionGrantTypedData(contractAddress, grant, networkConfig.chain.chainId);
  const workerAuthorization = await eip1193Provider.request({
    method: "eth_signTypedData_v4",
    params: [worker, JSON.stringify({
      domain: typedData.domain,
      types: { EIP712Domain: EIP712_DOMAIN_FIELDS, ...typedData.types },
      primaryType: typedData.primaryType,
      message: typedData.message
    })]
  });
  return {
    worker: grant.worker,
    grant,
    workerAuthorization,
    signer,
    createdAt: new Date().toISOString(),
    expiresAt: new Date(grant.validUntil * 1000).toISOString()
  };
}

export async function signWorkerSessionPayload(session, payload) {
  if (!session?.signer || !payload?.typedData) throw new Error("Worker session is not active.");
  if (Date.parse(session.expiresAt) <= Date.now()) throw new Error("Worker session has expired.");
  const { domain, types, message } = payload.typedData;
  return session.signer.signTypedData(domain, types, message);
}

export async function revokeWorkerSession(networkConfig, eip1193Provider, session) {
  if (!session?.grant) return null;
  const provider = new BrowserProvider(eip1193Provider);
  const signer = await provider.getSigner();
  const contract = new Contract(networkConfig.settlement.contractAddress, WORK_ESCROW_ABI, signer);
  const transaction = await contract.revokeSession(session.grant);
  const receipt = await transaction.wait(1);
  if (receipt.status !== 1) throw new Error("Worker-session revocation reverted.");
  return receipt.hash;
}
